import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Plus, X } from "lucide-react";

interface TagInputProps {
  label: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  inputType?: "text" | "number";
  inputClassName?: string;
  className?: string;
}

export function TagInput({
  label,
  tags,
  onChange,
  placeholder,
  inputType = "text",
  inputClassName,
  className,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState("");

  const addTag = () => {
    const val = inputType === "number" ? inputValue.trim() : inputValue.trim();
    if (!val) return;
    if (inputType === "number") {
      const num = parseInt(val);
      if (!isNaN(num)) {
        onChange([...tags, String(num)]);
      }
    } else {
      onChange([...tags, val]);
    }
    setInputValue("");
  };

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div className={className}>
      <Label className="mb-1.5 block text-xs text-overlay1">{label}</Label>
      <div className="flex gap-2">
        <Input
          type={inputType}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? `Add ${label.toLowerCase()}...`}
          className={inputClassName}
        />
        <Button type="button" variant="outline" size="sm" onClick={addTag} className="shrink-0">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tags.map((tag, i) => (
            <Badge key={i} variant="secondary" className="text-[11px] gap-1 pr-1">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(i)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-surface1 transition-colors"
              >
                <X className="h-2.5 w-2.5 text-overlay1 hover:text-red" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
